import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getApiUrl } from '../config/api';
import authFetch, { parseToken } from '../utils/authFetch';
import { roleLabel } from '../utils/roles';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Bell,
  MessageSquare,
  Shield,
  User,
  Lock,
  Monitor,
  Smartphone,
  Tablet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Phone,
  Copy,
  Check,
  Users,
  UserPlus,
  Search,
  MoreHorizontal,
  ShieldCheck,
  Clock,
  Mail,
  X,
  Building2,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// JWT role detection
// ---------------------------------------------------------------------------

function getUserRoleFromToken() {
  const payload = parseToken(localStorage.getItem('access_token'));
  return payload?.role || null;
}

// ---------------------------------------------------------------------------
// Copy-to-clipboard button
// ---------------------------------------------------------------------------

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-[#DDDDDD] bg-white hover:bg-gray-50 transition-colors shrink-0"
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="w-4 h-4 text-[#2ECC71]" />
      ) : (
        <Copy className="w-4 h-4 text-[#7F8C8D]" />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Phone number formatting helpers
// ---------------------------------------------------------------------------

function formatPhoneDisplay(digits) {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function parsePhoneDigits(formatted) {
  return formatted.replace(/\D/g, '').slice(0, 10);
}

// ---------------------------------------------------------------------------
// SMS Tab
// ---------------------------------------------------------------------------

function SmsTab() {
  const [transactionalSms, setTransactionalSms] = useState(false);
  const [tenantNotifications, setTenantNotifications] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savedPhoneNumber, setSavedPhoneNumber] = useState('');
  const [orgPhoneNumber, setOrgPhoneNumber] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState(null);
  const [toggleError, setToggleError] = useState(null);
  const [forwardingNumber, setForwardingNumber] = useState('');
  const [savedForwardingNumber, setSavedForwardingNumber] = useState('');
  const [savingForwarding, setSavingForwarding] = useState(false);
  const [forwardingMessage, setForwardingMessage] = useState(null);

  const isTenant = getUserRoleFromToken() === 'tenant';
  const phoneDirty = phoneNumber !== savedPhoneNumber;
  const forwardingDirty = forwardingNumber !== savedForwardingNumber;

  useEffect(() => {
    const fetchSmsData = async () => {
      try {
        const [accountRes, orgRes] = await Promise.all([
          authFetch(getApiUrl('/api/v1/settings/account')),
          authFetch(getApiUrl('/api/v1/organization/get')),
        ]);

        if (accountRes.ok) {
          const data = await accountRes.json();
          const digits = parsePhoneDigits((data.user?.phone || '').replace(/^\+1/, ''));
          setPhoneNumber(digits);
          setSavedPhoneNumber(digits);
          setTransactionalSms(data.user?.sms_consent_transactional || false);
          setTenantNotifications(data.user?.sms_tenant_notifications || false);
        }
        if (orgRes.ok) {
          const data = await orgRes.json();
          setOrgPhoneNumber(data.org?.messaging_number || '');
          const forwardingDigits = parsePhoneDigits((data.org?.phone || '').replace(/^\+1/, ''));
          setForwardingNumber(forwardingDigits);
          setSavedForwardingNumber(forwardingDigits);
        }
      } catch (err) {
        console.error('Error fetching SMS data:', err);
      }
    };

    fetchSmsData();
  }, []);

  // Optimistically flip a toggle and persist just that field; revert on failure.
  const handleToggle = async (field, setter, previousValue, nextValue) => {
    setter(nextValue);
    setToggleError(null);

    try {
      const response = await authFetch(getApiUrl('/api/v1/settings/account'), {
        method: 'PUT',
        body: JSON.stringify({ [field]: nextValue }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save setting.');
      }
    } catch (err) {
      console.error('Error saving setting:', err);
      setter(previousValue);
      setToggleError({ field, text: 'Could not save your change. Please try again.' });
    }
  };

  const handleSavePhone = async () => {
    try {
      setSavingPhone(true);
      setPhoneMessage(null);

      const response = await authFetch(getApiUrl('/api/v1/settings/account'), {
        method: 'PUT',
        body: JSON.stringify({ phone: `+1${phoneNumber}` }),
      });

      if (response.ok) {
        setSavedPhoneNumber(phoneNumber);
        setPhoneMessage({ type: 'success', text: 'Phone number saved successfully.' });
      } else {
        const errorData = await response.json();
        setPhoneMessage({ type: 'error', text: errorData.error || 'Failed to save phone number.' });
      }
    } catch (err) {
      console.error('Error saving phone number:', err);
      setPhoneMessage({ type: 'error', text: 'An error occurred while saving.' });
    } finally {
      setSavingPhone(false);
    }
  };

  const handleSaveForwarding = async () => {
    if (forwardingNumber && forwardingNumber.length !== 10) {
      setForwardingMessage({ type: 'error', text: 'Please enter a valid 10-digit phone number.' });
      return;
    }

    try {
      setSavingForwarding(true);
      setForwardingMessage(null);

      const response = await authFetch(getApiUrl('/api/v1/organization/update'), {
        method: 'POST',
        body: JSON.stringify({ phone: forwardingNumber ? `+1${forwardingNumber}` : '' }),
      });

      if (response.ok) {
        setSavedForwardingNumber(forwardingNumber);
        setForwardingMessage({
          type: 'success',
          text: forwardingNumber
            ? 'Forwarding number saved successfully.'
            : 'Forwarding number removed.',
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setForwardingMessage({ type: 'error', text: errorData.error || 'Failed to save forwarding number.' });
      }
    } catch (err) {
      console.error('Error saving forwarding number:', err);
      setForwardingMessage({ type: 'error', text: 'An error occurred while saving.' });
    } finally {
      setSavingForwarding(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Transactional SMS */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <div className="flex items-start gap-3 mb-1">
          <Shield className="w-5 h-5 text-[#3498DB] mt-0.5 shrink-0" />
          <div>
            <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
              Transactional SMS
            </h3>
            <p className="text-[#7F8C8D] text-sm mt-1">
              Security and critical account notifications sent by ZenRent (ex: 2FA, login alerts).
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0]">
            <div className="flex-1">
              <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                Receive SMS from ZenRent for account and transactional notifications
              </p>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Security alerts, account updates, and other important notifications via text message.
              </p>
            </div>
            <Switch
              checked={transactionalSms}
              onCheckedChange={(val) =>
                handleToggle('sms_consent_transactional', setTransactionalSms, transactionalSms, val)
              }
              className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
            />
          </div>

          <div className="flex items-start justify-between gap-4 py-3 border-b border-[#E2E8F0] opacity-60">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                  Two-factor authentication codes
                </p>
                <span className="text-[10px] px-1.5 py-0.5 bg-[#94A3B8]/10 text-[#94A3B8] rounded" style={{ fontWeight: 500 }}>
                  Coming soon
                </span>
              </div>
              <p className="text-xs text-[#7F8C8D] mt-0.5">
                Receive verification codes via SMS for secure login.
              </p>
            </div>
            <Switch
              checked={false}
              disabled
              className="mt-0.5"
            />
          </div>
        </div>

        {toggleError?.field === 'sms_consent_transactional' && (
          <p className="text-sm mt-4 text-[#E74C3C]" style={{ fontWeight: 500 }}>
            {toggleError.text}
          </p>
        )}

        <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
          <label className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
            My Phone Number
          </label>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-[40px]">
              <Input
                value="+1"
                readOnly
                className="bg-[#F1F5F9] text-[#64748B] cursor-not-allowed text-center"
              />
            </div>
            <Input
              value={formatPhoneDisplay(phoneNumber)}
              onChange={(e) => setPhoneNumber(parsePhoneDigits(e.target.value))}
              placeholder="(555) 000-0000"
              className="max-w-[200px]"
            />
            <button
              onClick={handleSavePhone}
              disabled={savingPhone || !phoneDirty}
              className="px-4 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              style={{ fontWeight: 500 }}
            >
              {savingPhone ? 'Saving...' : 'Save'}
            </button>
          </div>

          {phoneMessage && (
            <p
              className={`text-sm mt-2 ${
                phoneMessage.type === 'success' ? 'text-[#2ECC71]' : 'text-[#E74C3C]'
              }`}
              style={{ fontWeight: 500 }}
            >
              {phoneMessage.text}
            </p>
          )}
        </div>

        {!isTenant && (
          <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                  Notify me by SMS when a tenant sends a message
                </p>
                <p className="text-xs text-[#7F8C8D] mt-0.5">
                  Receive a text from ZenRent whenever one of your tenants sends a new message. Requires a phone number on file above.
                </p>
              </div>
              <Switch
                checked={tenantNotifications}
                onCheckedChange={(val) =>
                  handleToggle('sms_tenant_notifications', setTenantNotifications, tenantNotifications, val)
                }
                className="data-[state=checked]:bg-[#2ECC71] mt-0.5"
              />
            </div>
            {toggleError?.field === 'sms_tenant_notifications' && (
              <p className="text-sm mt-3 text-[#E74C3C]" style={{ fontWeight: 500 }}>
                {toggleError.text}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Rental Communications */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <div className="flex items-start gap-3 mb-1">
          <Phone className="w-5 h-5 text-[#2ECC71] mt-0.5 shrink-0" />
          <div>
            <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
              Rental Communications
            </h3>
            <p className="text-[#7F8C8D] text-sm mt-1">
              Your organization's text message settings for tenant communications. SMS messages to tenants are sent from your ZenRent phone number.
            </p>
          </div>
        </div>

        <div className="mt-6">
          <label className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
            My Organization's ZenRent Phone Number
          </label>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-[40px]">
              <Input
                value="+1"
                readOnly
                className="bg-[#F1F5F9] text-[#64748B] cursor-not-allowed text-center"
              />
            </div>
            <Input
              value={formatPhoneDisplay(orgPhoneNumber.replace(/^\+1/, ''))}
              readOnly
              className="max-w-[200px] bg-[#F1F5F9] text-[#64748B] cursor-not-allowed"
            />
            <CopyButton text={orgPhoneNumber} />
          </div>
        </div>

        {!isTenant && (
          <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
            <label className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
              My Organization's Call Forwarding Number
            </label>
            <p className="text-xs text-[#7F8C8D] mt-0.5">
              Phone calls to your ZenRent number will be forwarded to your organization's private number below. Your private number is not shared or sent to tenants.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <div className="w-[40px]">
                <Input
                  value="+1"
                  readOnly
                  className="bg-[#F1F5F9] text-[#64748B] cursor-not-allowed text-center"
                />
              </div>
              <Input
                value={formatPhoneDisplay(forwardingNumber)}
                onChange={(e) => setForwardingNumber(parsePhoneDigits(e.target.value))}
                placeholder="(555) 000-0000"
                className="max-w-[200px]"
              />
              <button
                onClick={handleSaveForwarding}
                disabled={savingForwarding || !forwardingDirty}
                className="px-4 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                style={{ fontWeight: 500 }}
              >
                {savingForwarding ? 'Saving...' : 'Save'}
              </button>
            </div>

            {forwardingMessage && (
              <p
                className={`text-sm mt-2 ${
                  forwardingMessage.type === 'success' ? 'text-[#2ECC71]' : 'text-[#E74C3C]'
                }`}
                style={{ fontWeight: 500 }}
              >
                {forwardingMessage.text}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security Tab
// ---------------------------------------------------------------------------

function SecurityTab() {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);

  const handleSendResetEmail = async () => {
    try {
      setSendingResetEmail(true);
      setPasswordMessage(null);

      const accountRes = await authFetch(getApiUrl('/api/v1/settings/account'));
      if (!accountRes.ok) {
        setPasswordMessage({ type: 'error', text: 'Could not retrieve account information.' });
        return;
      }
      const accountData = await accountRes.json();
      const email = accountData.user?.email;
      if (!email) {
        setPasswordMessage({ type: 'error', text: 'No email address found for this account.' });
        return;
      }

      const response = await fetch(getApiUrl('/api/v1/auth/password/forgot'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setPasswordMessage({ type: 'success', text: 'Password reset email sent. Check your inbox.' });
      } else {
        const data = await response.json();
        setPasswordMessage({ type: 'error', text: data.error || 'Failed to send reset email.' });
      }
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setPasswordMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setSendingResetEmail(false);
    }
  };

  const handleUpdatePassword = async () => {
    try {
      setSavingPassword(true);
      setPasswordMessage(null);

      const response = await authFetch(getApiUrl('/api/v1/settings/password'), {
        method: 'PUT',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordMessage({ type: 'success', text: 'Password updated successfully.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setChangePasswordOpen(false);
      } else {
        setPasswordMessage({ type: 'error', text: data.error || 'Failed to update password.' });
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordMessage({ type: 'error', text: 'An error occurred while updating password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const requirements = [
    { label: 'At least 8 characters', met: newPassword.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(newPassword) },
    { label: 'One lowercase letter', met: /[a-z]/.test(newPassword) },
    { label: 'One number', met: /[0-9]/.test(newPassword) },
    { label: 'One special character (!@#$%...)', met: /[^A-Za-z0-9]/.test(newPassword) },
  ];

  const metCount = requirements.filter((r) => r.met).length;
  const strengthPercent = (metCount / requirements.length) * 100;
  const strengthLabel =
    metCount === 0 ? '' : metCount <= 2 ? 'Weak' : metCount <= 4 ? 'Fair' : 'Strong';
  const strengthColor =
    metCount <= 2 ? '#E74C3C' : metCount <= 4 ? '#F39C12' : '#2ECC71';

  const passwordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <div className="space-y-6">
      {/* Password & Sign-in */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-5 h-5 text-[#3498DB]" />
          <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
            Password & Sign-in
          </h3>
        </div>
        <p className="text-[#7F8C8D] text-sm mt-1 mb-6">
          Manage your password and sign-in methods.
        </p>

        {passwordMessage && (
          <p
            className={`text-sm mb-4 ${
              passwordMessage.type === 'success' ? 'text-[#2ECC71]' : 'text-[#E74C3C]'
            }`}
            style={{ fontWeight: 500 }}
          >
            {passwordMessage.text}
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => {
              setChangePasswordOpen(!changePasswordOpen);
              setPasswordMessage(null);
            }}
            className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors"
            style={{ fontWeight: 500 }}
          >
            {changePasswordOpen ? 'Cancel' : 'Change password'}
          </button>
          <button
            onClick={handleSendResetEmail}
            disabled={sendingResetEmail}
            className="px-5 py-2 text-sm rounded-lg border border-[#DDDDDD] text-[#1E293B] bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontWeight: 500 }}
          >
            {sendingResetEmail ? 'Sending...' : 'Send password reset email'}
          </button>
        </div>

        {changePasswordOpen && (
          <div className="mt-6 pt-4 border-t border-[#E2E8F0] space-y-4 max-w-md">
            <div>
              <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
                Current password
              </label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
                New password
              </label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />

              {newPassword.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[#7F8C8D]">Password strength</span>
                    <span className="text-xs" style={{ color: strengthColor, fontWeight: 500 }}>
                      {strengthLabel}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${strengthPercent}%`, backgroundColor: strengthColor }}
                    />
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {requirements.map((req) => (
                      <li key={req.label} className="flex items-center gap-2 text-xs">
                        {req.met ? (
                          <CheckCircle className="w-3.5 h-3.5 text-[#2ECC71]" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-[#DDDDDD]" />
                        )}
                        <span className={req.met ? 'text-[#2ECC71]' : 'text-[#7F8C8D]'}>
                          {req.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
                Confirm new password
              </label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className={passwordMismatch ? 'border-[#E74C3C]' : ''}
              />
              {passwordMismatch && (
                <p className="flex items-center gap-1 text-xs text-[#E74C3C] mt-1.5">
                  <AlertTriangle className="w-3 h-3" />
                  Passwords do not match
                </p>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleUpdatePassword}
                disabled={
                  savingPassword || !currentPassword || !newPassword || !confirmPassword || passwordMismatch || newPassword.length < 8
                }
                className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontWeight: 500 }}
              >
                {savingPassword ? 'Updating...' : 'Update password'}
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Default organization section (shown only to multi-org members)
// ---------------------------------------------------------------------------

function DefaultOrgSection() {
  const [organizations, setOrganizations] = useState([]);
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const res = await authFetch(getApiUrl('/api/v1/organization/list'));
        if (res.ok) {
          const data = await res.json();
          const orgs = data.organizations || [];
          setOrganizations(orgs);
          const def = orgs.find((o) => o.is_default);
          if (def) setSelected(String(def.organization_id));
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
      }
    };

    fetchOrgs();
  }, []);

  const handleChange = async (value) => {
    const orgId = Number(value);
    const previous = selected;
    setSelected(value);
    setSaving(true);
    setMessage(null);

    try {
      const res = await authFetch(getApiUrl('/api/v1/organization/default'), {
        method: 'POST',
        body: JSON.stringify({ organization_id: orgId }),
      });

      if (res.ok) {
        setOrganizations((prev) =>
          prev.map((o) => ({ ...o, is_default: o.organization_id === orgId }))
        );
        setMessage({ type: 'success', text: 'Default organization updated.' });
      } else {
        const data = await res.json().catch(() => ({}));
        setSelected(previous);
        setMessage({ type: 'error', text: data.error || 'Failed to update default organization.' });
      }
    } catch (err) {
      console.error('Error updating default organization:', err);
      setSelected(previous);
      setMessage({ type: 'error', text: 'An error occurred while saving.' });
    } finally {
      setSaving(false);
    }
  };

  // Single-org members see no organization controls (same rule as the switcher).
  if (organizations.length <= 1) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-5 h-5 text-[#3498DB]" />
        <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
          Organization
        </h3>
      </div>
      <p className="text-[#7F8C8D] text-sm mt-1 mb-6">
        Default organization — used when you log in and when you text appliance photos to ZenRent.
      </p>

      <RadioGroup value={selected} onValueChange={handleChange} disabled={saving}>
        {organizations.map((org) => (
          <label
            key={org.organization_id}
            htmlFor={`default-org-${org.organization_id}`}
            className="flex items-center gap-3 py-2 cursor-pointer"
          >
            <RadioGroupItem
              value={String(org.organization_id)}
              id={`default-org-${org.organization_id}`}
              className="border-[#2ECC71] text-[#2ECC71]"
            />
            <span className="flex-1 min-w-0">
              <span className="text-sm text-[#1E293B]" style={{ fontWeight: 500 }}>
                {org.org_name}
              </span>
              <span className="block text-xs text-[#7F8C8D]">{roleLabel(org.role)}</span>
            </span>
          </label>
        ))}
      </RadioGroup>

      {message && (
        <p
          className={`text-sm mt-4 ${
            message.type === 'success' ? 'text-[#2ECC71]' : 'text-[#E74C3C]'
          }`}
          style={{ fontWeight: 500 }}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account Tab
// ---------------------------------------------------------------------------

function AccountTab() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setSaveMessage(null);

      const response = await authFetch(getApiUrl('/api/v1/settings/account'), {
        method: 'PUT',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
        }),
      });

      if (response.ok) {
        setSaveMessage({ type: 'success', text: 'Profile saved successfully.' });
        localStorage.setItem('first_name', firstName);
      } else {
        const errorData = await response.json();
        setSaveMessage({ type: 'error', text: errorData.error || 'Failed to save profile.' });
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setSaveMessage({ type: 'error', text: 'An error occurred while saving.' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const response = await authFetch(getApiUrl('/api/v1/settings/account'));

        if (response.ok) {
          const data = await response.json();
          setFirstName(data.user?.first_name || '');
          setLastName(data.user?.last_name || '');
          setEmail(data.user?.email || '');
        }
      } catch (err) {
        console.error('Error fetching account settings:', err);
      }
    };

    fetchAccount();
  }, []);

  return (
    <div className="space-y-6">
      {/* Profile */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-6">
        <div className="flex items-center gap-2 mb-1">
          <User className="w-5 h-5 text-[#3498DB]" />
          <h3 className="text-[#2C3E50]" style={{ fontSize: '18px', fontWeight: 600 }}>
            Profile
          </h3>
        </div>
        <p className="text-[#7F8C8D] text-sm mt-1 mb-6">
          Your personal information and preferences.
        </p>

        <div className="space-y-5 max-w-lg">
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
                First name
              </label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm text-[#1E293B] mb-1.5 block" style={{ fontWeight: 500 }}>
                Last name
              </label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-[#1E293B] mb-1.5 flex items-center gap-1.5" style={{ fontWeight: 500 }}>
              Email
              <Lock className="w-3 h-3 text-[#7F8C8D]" />
            </label>
            <Input
              value={email}
              readOnly
              className="bg-[#F1F5F9] text-[#64748B] cursor-not-allowed"
            />
            <p className="text-xs text-[#7F8C8D] mt-1">Email cannot be changed.</p>
          </div>

        </div>

        {saveMessage && (
          <p
            className={`text-sm mt-4 ${
              saveMessage.type === 'success' ? 'text-[#2ECC71]' : 'text-[#E74C3C]'
            }`}
            style={{ fontWeight: 500 }}
          >
            {saveMessage.text}
          </p>
        )}

        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-[#E2E8F0]">
          <button
            onClick={() => setSaveMessage(null)}
            className="px-5 py-2 text-sm rounded-lg border border-[#DDDDDD] text-[#7F8C8D] bg-white hover:bg-gray-50 transition-colors"
            style={{ fontWeight: 500 }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="px-5 py-2 text-sm rounded-lg bg-[#2ECC71] text-white hover:bg-[#27AE60] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontWeight: 500 }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>

      <DefaultOrgSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// User Management Tab (org_admin only)
// ---------------------------------------------------------------------------

const AVATAR_COLORS = ['#3498DB', '#E67E22', '#9B59B6', '#1ABC9C', '#E74C3C'];

function UserManagementTab() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('landlord');
  const [inviteState, setInviteState] = useState('idle'); // idle | sending | sent | error
  const [inviteError, setInviteError] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  const fetchMembers = async () => {
    try {
      const res = await authFetch(getApiUrl('/api/v1/organization/members'));
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  const filtered = members.filter((m) => {
    const fullName = `${m.first_name} ${m.last_name}`.toLowerCase();
    const matchesSearch =
      fullName.includes(searchQuery.toLowerCase()) ||
      m.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || m.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleSendInvite = async () => {
    if (!inviteEmail) return;

    setInviteState('sending');
    setInviteError('');

    try {
      const res = await authFetch(getApiUrl('/api/v1/organization/invite'), {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });

      if (res.ok) {
        setInviteState('sent');
      } else {
        const data = await res.json();
        setInviteError(data.error || 'Failed to send invitation.');
        setInviteState('error');
      }
    } catch (err) {
      console.error('Error sending invite:', err);
      setInviteError('An error occurred. Please try again.');
      setInviteState('error');
    }
  };

  const handleCloseInviteDialog = (open) => {
    if (!open) {
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('landlord');
      setInviteState('idle');
      setInviteError('');
    }
  };

  const handleRemoveUser = async (memberId) => {
    try {
      const res = await authFetch(getApiUrl(`/api/v1/organization/members/${memberId}`), {
        method: 'DELETE',
      });
      if (res.ok) {
        setMembers((prev) => prev.filter((m) => m.id !== memberId));
      }
    } catch (err) {
      console.error('Error removing member:', err);
    }
    setOpenMenuId(null);
  };

  const currentUserId = (() => {
    const payload = parseToken(localStorage.getItem('access_token'));
    return payload ? Number(payload.sub) : null;
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-[#94A3B8] text-sm">Loading members...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Members card */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm">
        {/* Card header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
          <div>
            <h2
              className="text-[#2C3E50]"
              style={{ fontSize: '18px', fontWeight: 600 }}
            >
              Organization Members
            </h2>
            <p className="text-[#7F8C8D] text-sm mt-0.5">
              Manage landlords and administrators in your organization.
            </p>
          </div>
          <button
            onClick={() => setInviteOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#2ECC71] text-white rounded-lg text-sm hover:bg-[#27AE60] transition-colors shadow-sm"
            style={{ fontWeight: 500 }}
          >
            <UserPlus className="w-4 h-4" />
            Invite Member
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 p-4 border-b border-[#E2E8F0] bg-[#FAFBFC]">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 bg-white border-[#E2E8F0] text-sm"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[140px] h-9 bg-white border-[#E2E8F0] text-sm">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="org_admin">Admin</SelectItem>
              <SelectItem value="landlord">Landlord</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E2E8F0] bg-[#FAFBFC]">
                {['Member', 'Role', 'Status', 'Joined', ''].map((h) => (
                  <th
                    key={h || 'actions'}
                    className="text-left px-6 py-3 text-[#7F8C8D] text-xs"
                    style={{ fontWeight: 500 }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((member, idx) => (
                <tr
                  key={member.id}
                  className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8FAFC] transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm shrink-0"
                        style={{
                          backgroundColor: AVATAR_COLORS[idx % AVATAR_COLORS.length],
                          fontWeight: 600,
                        }}
                      >
                        {(member.first_name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p
                          className="text-[#2C3E50] text-sm"
                          style={{ fontWeight: 500 }}
                        >
                          {member.first_name} {member.last_name}
                        </p>
                        <p className="text-[#94A3B8] text-xs">{member.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${
                        member.role === 'org_admin'
                          ? 'bg-[#EBF5FB] text-[#2980B9]'
                          : 'bg-[#F0F0F0] text-[#5D6D7E]'
                      }`}
                      style={{ fontWeight: 500 }}
                    >
                      {member.role === 'org_admin' ? (
                        <ShieldCheck className="w-3 h-3" />
                      ) : (
                        <Shield className="w-3 h-3" />
                      )}
                      {member.role === 'org_admin' ? 'Admin' : 'Landlord'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${
                        member.is_active
                          ? 'bg-[#E8F8F0] text-[#27AE60]'
                          : 'bg-[#FEF9E7] text-[#F39C12]'
                      }`}
                      style={{ fontWeight: 500 }}
                    >
                      {member.is_active ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Clock className="w-3 h-3" />
                      )}
                      {member.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[#7F8C8D] text-sm">
                    {member.created_at
                      ? new Date(member.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="px-6 py-4">
                    {member.id !== currentUserId && (
                      <div className="relative">
                        <button
                          onClick={() =>
                            setOpenMenuId(openMenuId === member.id ? null : member.id)
                          }
                          className="p-1.5 rounded-md hover:bg-[#F1F5F9] transition-colors"
                        >
                          <MoreHorizontal className="w-4 h-4 text-[#94A3B8]" />
                        </button>
                        {openMenuId === member.id && (
                          <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg border border-[#E2E8F0] shadow-lg z-10 py-1">
                            <button
                              onClick={() => handleRemoveUser(member.id)}
                              className="w-full text-left px-4 py-2 text-sm text-[#E74C3C] hover:bg-[#FEF2F2] flex items-center gap-2"
                            >
                              <X className="w-3.5 h-3.5" />
                              Remove Member
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <p className="text-[#94A3B8] text-sm">
                      {members.length === 0
                        ? 'No members yet. Invite someone to get started.'
                        : 'No members match your filters.'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={handleCloseInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-[#2C3E50]"
              style={{ fontSize: '18px', fontWeight: 600 }}
            >
              Invite a Member
            </DialogTitle>
            <DialogDescription className="text-[#7F8C8D] text-sm">
              Send an invitation to join your organization.
            </DialogDescription>
          </DialogHeader>

          {inviteState === 'sent' ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <div className="w-12 h-12 rounded-full bg-[#E8F8F0] flex items-center justify-center">
                <Check className="w-6 h-6 text-[#2ECC71]" />
              </div>
              <p
                className="text-[#2C3E50] text-sm text-center"
                style={{ fontWeight: 500 }}
              >
                Invitation sent!
              </p>
              <p className="text-[#7F8C8D] text-xs text-center max-w-xs">
                {inviteEmail} will appear here after they sign up.
              </p>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-[#2C3E50] text-sm">Email Address</Label>
                <Input
                  type="email"
                  placeholder="colleague@email.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="border-[#E2E8F0]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2C3E50] text-sm">Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger className="border-[#E2E8F0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landlord">Landlord</SelectItem>
                    <SelectItem value="org_admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[#94A3B8] text-xs">
                  Admins can manage members and organization settings.
                </p>
              </div>
              {inviteState === 'error' && (
                <p className="text-sm text-[#E74C3C]" style={{ fontWeight: 500 }}>
                  {inviteError}
                </p>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => handleCloseInviteDialog(false)}
                  className="px-4 py-2 text-sm text-[#7F8C8D] hover:text-[#2C3E50] transition-colors rounded-lg hover:bg-[#F1F5F9]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={!inviteEmail || inviteState === 'sending'}
                  className="flex items-center gap-2 px-4 py-2 bg-[#2ECC71] text-white rounded-lg text-sm hover:bg-[#27AE60] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontWeight: 500 }}
                >
                  <Mail className="w-4 h-4" />
                  {inviteState === 'sending' ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings Page (main)
// ---------------------------------------------------------------------------

function Settings() {
  const [activeTab, setActiveTab] = useState('sms');

  const tokenRole = getUserRoleFromToken();
  const isAdmin = tokenRole === 'org_admin';

  const tabs = [
    { id: 'sms', label: 'Communication', icon: MessageSquare },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'account', label: 'Account', icon: User },
    ...(isAdmin ? [{ id: 'users', label: 'Members', icon: Users }] : []),
  ];

  return (
    <Layout pageTitle="Settings" pageSubtitle="Manage notifications, messaging, and security.">
      {/* Tab navigation */}
      <div className="flex gap-1 mb-6 bg-white rounded-lg border border-[#E2E8F0] p-1 w-fit shadow-sm">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-[#2ECC71] text-white shadow-sm'
                  : 'text-[#7F8C8D] hover:text-[#2C3E50] hover:bg-[#F1F5F9]'
              }`}
              style={{ fontWeight: activeTab === tab.id ? 500 : 400 }}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className={activeTab === 'users' ? 'max-w-5xl' : 'max-w-3xl'}>
        {activeTab === 'sms' && <SmsTab />}
        {activeTab === 'security' && <SecurityTab />}
        {activeTab === 'account' && <AccountTab />}
        {activeTab === 'users' && isAdmin && <UserManagementTab />}
      </div>
    </Layout>
  );
}

export default Settings;
